import { ReactComponent as TiktokSocial } from "assets/images/tiktokSocial.svg";

import {
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { ACCESS_TOKEN, PRIVY_TOKEN, getItem } from "local-storage";
import {
  confirmTiktokCode,
  delegateBuyFirstShare,
  generateTiktokCode,
  getConfigTiktok,
  getTwitterAuthLoginUrl,
  getUserProfileData,
  postLoginTwitter,
} from "services/apis/apis";
import slugs from "navigation/slugs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useAuth } from "contexts/authContext";
import { Loading, Modal } from "components/commons";
import { ReactComponent as Gift } from "assets/icons/coming-soon.svg";

const MIN_FOLLOWERS = 1000;

function AccountVerify() {
  const navigate = useNavigate();
  const location = useLocation();

  const [seconds, setSeconds] = useState(0);
  const [tiktokCode, setTiktokCode] = useState("");
  const [tiktokLinkVideo, setTiktokLinkVideo] = useState("");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifing, setIsVerifing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const {
    updateIsVerifyTiktok,
    updateIsBuyFirstShares,
    updateIsDepositEth,
    updateToken,
    updateReferrerId,
  } = useAuth();

  const approved = () => {
    setIsLoading(false);
    updateIsVerifyTiktok("IsVerifiedTiktok");
  };

  const handleVerifyCode = async () => {
    try {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      const url = await getTwitterAuthLoginUrl(accessToken);
      window.location.href = url;
    } catch (error) {
      console.log(
        "🚀 ~ file: AccountVerify.tsx:54 ~ handleVerifyCode ~ error:",
        error
      );
    }
  };

  useEffect(() => {
    const generateTiktok = async () => {
      try {
        const accessToken = getItem(ACCESS_TOKEN) || "";

        const config = await getConfigTiktok();
        setTiktokLinkVideo(config?.data?.link);

        if (!accessToken) return;

        const userProfile = await getUserProfileData(accessToken);

        if (userProfile.uid) {
          // DELEGATE INITIALIZE SHARES FOR KOL

          if (userProfile?.tiktok_user_info?.follower_count > MIN_FOLLOWERS) {
            try {
              await delegateBuyFirstShare(accessToken);
              setIsLoading(false);
              toggleModal();
            } catch (error) {
              approved();
              console.log(
                "🚀 ~ file: AccountVerify.tsx:52 ~ delegateBuyFirstShare ~ error:",
                error
              );
            }
          } else {
            approved();
          }
        } else {
          const res = await generateTiktokCode(accessToken);
          setTiktokCode(res);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    };

    generateTiktok();
  }, [navigate, updateIsVerifyTiktok]);

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  useEffect(() => {
    if (seconds === 0) return;
    const timer = setInterval(() => {
      setSeconds((prevSeconds) => prevSeconds - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [seconds]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get("code");
    const state = queryParams.get("state");
    const privyToken = JSON.parse(getItem(PRIVY_TOKEN) || "");

    if (!code || !state || !privyToken) return;

    const f = async () => {
      const res = await postLoginTwitter(privyToken, code, state);
      if (res?.data?.data?.referrer_id) {
        updateToken(res?.data?.data?.access_token);
        updateReferrerId(res.data.data.referrer_id);
      } else {
        updateToken(res?.data?.data?.access_token);
      }
    };
    f();
  }, [location.search]);

  return (
    <>
      <Modal
        readonly={true}
        isOpen={isOpen}
        toggleModal={toggleModal}
        position="center"
      >
        <div className=" text-center flex flex-col bg-white p-10 m-6 rounded-xl gap-4 shadow justify-center items-center text-sm font-normal text-gray-700">
          <Gift className="w-32" />
          <span className=" text-xl text-gray-900">Congratulations! </span>
          <span>
            Embracing your brilliance as an emerging talent, we're thrilled to
            present you with a special gift: Your very first share! 🎁
          </span>
          <span className="">The sky's your limit on fan.tech!</span>

          <button
            className="rounded-xl btn flex-1 bg-secondary uppercase border-none"
            onClick={(e) => {
              e.preventDefault();
              updateIsVerifyTiktok("IsVerifiedTiktok");
              updateIsDepositEth("IsDepositedEth");
              updateIsBuyFirstShares("IsBuyedFirstShares");
              toggleModal();
              navigate(slugs.profile, { replace: true });
            }}
          >
            Go to my profile
          </button>
        </div>
      </Modal>
      {isLoading ? (
        <Loading />
      ) : (
        <div className=" min-h-screen flex-1 flex flex-col justify-between w-full h-full p-4">
          <div className="flex flex-col gap-6">
            <TiktokSocial className="w-24" />

            <div className="flex flex-col gap-4 text-sm text-slate-300">
              <h2 className="text-2xl text-white">Link your TikTok Account</h2>
              <p className="">
                Link your TikTok account to confirm your identity, making it
                simpler for fans & friends to find and exchange your Shares.
              </p>
              <b className="font-bold">Step-by-step guide:</b>

              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center font-normal">
                  <div className="w-5 h-5 rounded-full flex justify-center items-center bg-[#279EFF] font-medium text-xs">
                    1
                  </div>
                  Copy the code below
                </div>
                <div className="flex gap-2 items-center font-normal">
                  <div className="w-5 h-5 rounded-full flex justify-center items-center bg-[#279EFF] font-medium text-xs">
                    2
                  </div>
                  <span>
                    Comment the code under this Tiktok{" "}
                    <a
                      target="blank"
                      className="text-secondary"
                      href={tiktokLinkVideo}
                    >
                      video{" "}
                      <ArrowTopRightOnSquareIcon className="w-3 inline mb-1" />
                    </a>{" "}
                  </span>
                </div>
                <div className="flex gap-2 items-center font-normal">
                  <div className="w-5 h-5 rounded-full flex justify-center items-center bg-[#279EFF] font-medium text-xs">
                    3
                  </div>
                  Click “Verify”
                </div>
                <div className="flex gap-2 items-center font-normal">
                  <div className="w-5 h-5 rounded-full flex justify-center items-center bg-[#279EFF] font-medium text-xs">
                    4
                  </div>
                  Wait around 2 minutes for data fetching{" "}
                </div>
              </div>
            </div>

            <div className="relative mt-2 flex items-center">
              <input
                className="w-full py-4 px-[14px] pr-10 border-2 border-slate-700 bg-transparent rounded-xl focus:outline-none text-slate-300 text-sm"
                value={tiktokCode}
                readOnly={true}
              />

              <div className="absolute inset-y-0 right-0 py-4 pr-6">
                <CopyToClipboard
                  text={tiktokCode}
                  onCopy={() => {
                    if (isVerifing || isLoading || !tiktokCode) return;
                    setIsCopied(true);
                  }}
                >
                  <button
                    className={`text-xs flex gap-1 `}
                    disabled={isVerifing || isLoading || !tiktokCode}
                  >
                    <DocumentDuplicateIcon className="stroke-white stroke-2 text-white w-4" />{" "}
                    {isCopied ? "Copied" : "Copy"}
                  </button>
                </CopyToClipboard>
              </div>
            </div>

            {seconds < 60 && seconds > 0 && (
              <div className="text-xs mb-6 -mt-2">
                <p className="text-warning">
                  <i className="fa-solid fa-circle-exclamation mr-2"></i>
                  Verification may take up to 2 minutes. Tiktok may filter
                  comment as spam, <b>re-comment</b> & verify again!
                </p>
              </div>
            )}
          </div>
          <button
            className="rounded-xl btn w-full bg-secondary "
            onClick={(e) => {
              e.preventDefault();
              handleVerifyCode();
            }}
            // disabled={isVerifing || isLoading || !tiktokCode}
          >
            {isVerifing ? (
              <span className="flex items-center gap-2">
                <i className="loading w-5"></i> VERIFYING...
                {seconds === 0
                  ? null
                  : `( ${minutes}m : ${remainingSeconds}s )`}
              </span>
            ) : (
              "VERIFY"
            )}
          </button>
        </div>
      )}
    </>
  );
}

export { AccountVerify as default };
